import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { 
  Account, 
  AccountContextType, 
  AccountType, 
  CreateAccountRequest 
} from '../types';
import { getAccounts, createAccount as apiCreateAccount } from '../services/api';
import { useAuth } from './AuthContext';

const AccountContext = createContext<AccountContextType | undefined>(undefined);

interface AccountProviderProps {
  children: ReactNode;
}

const SELECTED_ACCOUNT_KEY = 'cotune_selected_account';

export const AccountProvider: React.FC<AccountProviderProps> = ({ children }) => {
  const { isAuthenticated, isEmailVerified, loading: authLoading } = useAuth();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Computed properties
  const isManager = accounts.some(acc => acc.type === 'manager');
  const isMultiAccount = accounts.length >= 2;
  // Only report hasNoAccounts as true after both auth and accounts loading is complete
  const hasNoAccounts = !authLoading && !isLoading && accounts.length === 0;
  
  // Get the primary account type (for routing decisions)
  const primaryAccountType: AccountType | null = (() => {
    if (accounts.length === 0) return null;
    // If user has a manager account, treat them as manager
    const managerAccount = accounts.find(acc => acc.type === 'manager');
    if (managerAccount) return 'manager';
    // Otherwise return the type of their first account
    return accounts[0]?.type || null;
  })();

  // Load accounts when authenticated
  const refreshAccounts = useCallback(async () => {
    // Wait for auth to finish loading before making decisions
    if (authLoading) {
      return;
    }
    
    if (!isAuthenticated || !isEmailVerified) {
      setAccounts([]);
      setSelectedAccount(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const fetchedAccounts = await getAccounts();
      setAccounts(fetchedAccounts);

      // Restore selected account from localStorage or select first one
      const savedAccountId = localStorage.getItem(SELECTED_ACCOUNT_KEY);
      if (savedAccountId) {
        const savedAccount = fetchedAccounts.find(acc => acc.id === savedAccountId);
        if (savedAccount) {
          setSelectedAccount(savedAccount);
        } else if (fetchedAccounts.length > 0) {
          // Saved account no longer exists, select first
          setSelectedAccount(fetchedAccounts[0]);
          localStorage.setItem(SELECTED_ACCOUNT_KEY, fetchedAccounts[0].id);
        }
      } else if (fetchedAccounts.length > 0) {
        // No saved selection, select first account
        setSelectedAccount(fetchedAccounts[0]);
        localStorage.setItem(SELECTED_ACCOUNT_KEY, fetchedAccounts[0].id);
      }
    } catch (err) {
      console.error('Failed to load accounts:', err);
      setError('Failed to load accounts');
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, isEmailVerified, authLoading]);

  useEffect(() => {
    refreshAccounts();
  }, [refreshAccounts]);

  // Select an account (null for "all accounts" view for managers)
  const selectAccount = useCallback((accountId: string | null) => {
    if (accountId === null) {
      setSelectedAccount(null);
      localStorage.removeItem(SELECTED_ACCOUNT_KEY);
    } else {
      const account = accounts.find(acc => acc.id === accountId);
      if (account) {
        setSelectedAccount(account);
        localStorage.setItem(SELECTED_ACCOUNT_KEY, accountId);
      }
    }
  }, [accounts]);

  // Create a new account
  const createAccount = useCallback(async (data: CreateAccountRequest): Promise<Account> => {
    try {
      const response = await apiCreateAccount(data);
      const newAccount = response.account;
      
      // Add to local state
      setAccounts(prev => [...prev, newAccount]);
      
      // Select the new account
      setSelectedAccount(newAccount);
      localStorage.setItem(SELECTED_ACCOUNT_KEY, newAccount.id);
      
      return newAccount;
    } catch (err) {
      console.error('Failed to create account:', err);
      throw err;
    }
  }, []);

  const value: AccountContextType = {
    accounts,
    selectedAccount,
    isLoading,
    error,
    isManager,
    isMultiAccount,
    hasNoAccounts,
    primaryAccountType,
    selectAccount,
    refreshAccounts,
    createAccount,
  };

  return (
    <AccountContext.Provider value={value}>
      {children}
    </AccountContext.Provider>
  );
};

export const useAccount = (): AccountContextType => {
  const context = useContext(AccountContext);
  if (context === undefined) {
    throw new Error('useAccount must be used within an AccountProvider');
  }
  return context;
};

export default AccountContext;
